// Per-page renderer for "spend-analysis" — extracted from reportBuilder.js.
// Signature: async (api, doc, ctx) => { y, aiInsights, aiRecommendations }
// Uses the SAME endpoint (/analytics/spend-deep-dive) as the SpendAnalysis UI
// page so the PDF reflects exactly what the user sees on screen.
import autoTable from 'jspdf-autotable';
import { NAVY, fetchPageAI } from '../utils';
import { drawVerticalBarChart, drawDonutChart } from '../charts';

export async function renderSpendAnalysis(api, doc, ctx) {
  const { qs, filterDesc, cs, pw, page, filters } = ctx;
  let y = ctx.y;
  let aiInsights = [];
  let aiRecommendations = [];

  const res = await api.get(`/analytics/spend-deep-dive${qs ? `?${qs}` : ''}`);
  const dd = res.data || {};
  const totalSpend = dd.total_spend || 0;
  const catDepth = dd.category_depth || [];
  const spendBySbu = dd.spend_by_sbu || [];
  const spendByCountry = dd.spend_by_country || [];
  const supFrag = dd.supplier_fragmentation || {};

  // Context for AI = the same KPIs shown on the UI.
  // NB: spend-deep-dive returns category_depth rows with `total_spend` + `txn_count`.
  const catLines = catDepth.slice(0, 8).map(c => {
    const spend = c.total_spend || 0;
    return `- ${c.category}: ${cs}${spend.toLocaleString()} (${totalSpend ? (spend / totalSpend * 100).toFixed(1) : 0}%) / ${c.txn_count || 0} txns`;
  }).join('\n');
  const sbuLines = spendBySbu.slice(0, 5).map(s => `- ${s.name || s.sbu}: ${cs}${(s.spend || 0).toLocaleString()}`).join('\n');
  const dataContext = `Total Spend: ${cs}${totalSpend.toLocaleString()}\nTransactions: ${dd.total_transactions || 0}\nSuppliers: ${dd.total_suppliers || 0}\nAvg Transaction: ${cs}${(dd.avg_transaction_value || 0).toLocaleString()}\nTop suppliers (HHI): ${(supFrag.hhi || 0).toFixed(0)}\n\nCategory Depth:\n${catLines}\n\nSBUs:\n${sbuLines}`;

  try {
    const aiRes = await fetchPageAI(api, { page, dataContext, filterDesc, filters });
    aiInsights = aiRes.insights;
    aiRecommendations = aiRes.recommendations;
  } catch {}

  // KPI strip mirroring the 4 UI KpiCards
  autoTable(doc, {
    startY: y,
    head: [['Total Spend', 'Transactions', 'Suppliers', 'Avg Txn']],
    body: [[
      `${cs}${totalSpend.toLocaleString()}`,
      String(dd.total_transactions || 0),
      String(dd.total_suppliers || 0),
      `${cs}${(dd.avg_transaction_value || 0).toLocaleString()}`,
    ]],
    styles: { fontSize: 9, halign: 'center' },
    headStyles: { fillColor: NAVY },
    margin: { left: 14, right: 14 },
  });
  y = doc.lastAutoTable.finalY + 8;

  // Top categories (same sort as UI's category_depth).
  // NB: rows carry `total_spend` + `txn_count` — not `spend` + `transaction_count`.
  if (catDepth.length > 0) {
    const top = catDepth.slice(0, 10);
    const chartData = top.map(c => ({ label: c.category || 'Unknown', value: c.total_spend || 0 }));
    y = drawVerticalBarChart(doc, 14, y, pw - 28, 70, chartData, { title: 'Top Categories by Spend' });

    if (y > 190) { doc.addPage(); y = 15; }
    drawDonutChart(doc, 50, y + 30, 24, chartData.slice(0, 8), { title: 'Category Share' });
    y += 75;

    autoTable(doc, {
      startY: y,
      head: [['Category', 'Spend', 'Transactions', '% of Total']],
      body: top.map(c => [
        c.category || 'Unknown',
        `${cs}${(c.total_spend || 0).toLocaleString()}`,
        String(c.txn_count || 0),
        totalSpend ? `${((c.total_spend || 0) / totalSpend * 100).toFixed(1)}%` : '0%',
      ]),
      styles: { fontSize: 7 }, headStyles: { fillColor: NAVY },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // Spend by SBU — matches the UI's SBU panel.
  // spend_by_sbu uses `name` + `spend` (different convention to category_depth).
  if (spendBySbu.length > 0) {
    if (y > 200) { doc.addPage(); y = 15; }
    autoTable(doc, {
      startY: y,
      head: [['SBU', 'Spend', '% of Total']],
      body: spendBySbu.slice(0, 10).map(s => [
        s.name || s.sbu || 'Unknown',
        `${cs}${(s.spend || 0).toLocaleString()}`,
        totalSpend ? `${((s.spend || 0) / totalSpend * 100).toFixed(1)}%` : '0%',
      ]),
      styles: { fontSize: 7 }, headStyles: { fillColor: NAVY },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // Spend by country — matches the UI's geography panel
  if (spendByCountry.length > 0) {
    if (y > 200) { doc.addPage(); y = 15; }
    autoTable(doc, {
      startY: y,
      head: [['Country / Entity', 'Spend', '% of Total', 'Transactions']],
      body: spendByCountry.slice(0, 15).map(c => [
        c.name || c.country || 'Unknown',
        `${cs}${(c.spend || 0).toLocaleString()}`,
        `${(c.pct || 0).toFixed(1)}%`,
        String(c.count || 0),
      ]),
      styles: { fontSize: 7 }, headStyles: { fillColor: NAVY },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  return { y, aiInsights, aiRecommendations };
}
